/**
 * Canonical enumerations, transcribed from doc 02 §2, doc 03 §6, doc 04 §2.2.
 *
 * Rules for this file:
 *  - data only. No transitions, no guards, no engine logic. P2 owns behaviour.
 *  - `as const` objects, not TypeScript `enum`s: enums emit runtime code and
 *    `erasableSyntaxOnly` forbids them. The const object gives the same
 *    ergonomics (`OrderSide.BUY`), a literal-union type, and an iterable tuple.
 *  - adding a member is a spec change. Doc 02 §2 is frozen.
 */

/** doc 02 §2.1. Terminal states: `filled`, `canceled`, `rejected`, `expired`. */
export const OrderState = {
  RECEIVED: 'RECEIVED',
  ACTIVE: 'ACTIVE',
  PARTIALLY_FILLED: 'PARTIALLY_FILLED',
  FILLED: 'FILLED',
  CANCELED: 'CANCELED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
} as const;
export type OrderState = (typeof OrderState)[keyof typeof OrderState];
export const ORDER_STATES = Object.values(OrderState) as readonly OrderState[];

/** doc 02 §2.1. A terminal order has no reserved margin (I-9). */
export const TERMINAL_ORDER_STATES = [
  OrderState.FILLED,
  OrderState.CANCELED,
  OrderState.REJECTED,
  OrderState.EXPIRED,
] as const satisfies readonly OrderState[];

/** doc 02 §2 (order direction). */
export const OrderSide = {
  BUY: 'BUY',
  SELL: 'SELL',
} as const;
export type OrderSide = (typeof OrderSide)[keyof typeof OrderSide];
export const ORDER_SIDES = Object.values(OrderSide) as readonly OrderSide[];

/** doc 02 §2.2. */
export const OrderType = {
  MARKET: 'MARKET',
  LIMIT: 'LIMIT',
  STOP: 'STOP',
  STOP_LIMIT: 'STOP_LIMIT',
} as const;
export type OrderType = (typeof OrderType)[keyof typeof OrderType];
export const ORDER_TYPES = Object.values(OrderType) as readonly OrderType[];

/** doc 02 §2.3. */
export const TimeInForce = {
  GTC: 'GTC',
  DAY: 'DAY',
  IOC: 'IOC',
  FOK: 'FOK',
  SPECIFIED: 'SPECIFIED',
} as const;
export type TimeInForce = (typeof TimeInForce)[keyof typeof TimeInForce];
export const TIME_IN_FORCES = Object.values(TimeInForce) as readonly TimeInForce[];

/** doc 02 §2.4. `REQUOTE` is a response, not a state (decision B3). */
export const ExecutionMode = {
  INSTANT: 'INSTANT',
  MARKET: 'MARKET',
  REQUEST: 'REQUEST',
  EXCHANGE: 'EXCHANGE',
} as const;
export type ExecutionMode = (typeof ExecutionMode)[keyof typeof ExecutionMode];
export const EXECUTION_MODES = Object.values(ExecutionMode) as readonly ExecutionMode[];

/** doc 02 §2.5. */
export const FillPolicy = {
  FILL_OR_KILL: 'FILL_OR_KILL',
  IMMEDIATE_OR_CANCEL: 'IMMEDIATE_OR_CANCEL',
  RETURN: 'RETURN',
} as const;
export type FillPolicy = (typeof FillPolicy)[keyof typeof FillPolicy];
export const FILL_POLICIES = Object.values(FillPolicy) as readonly FillPolicy[];

/** doc 02 §2.6. Sub-state of a resting pending order; not an order state. */
export const TriggerState = {
  WAITING: 'WAITING',
  TRIGGERED: 'TRIGGERED',
  ARMED_LIMIT: 'ARMED_LIMIT',
} as const;
export type TriggerState = (typeof TriggerState)[keyof typeof TriggerState];
export const TRIGGER_STATES = Object.values(TriggerState) as readonly TriggerState[];

/** doc 02 §2.7. */
export const DealType = {
  OPEN: 'OPEN',
  CLOSE: 'CLOSE',
  CLOSE_BY: 'CLOSE_BY',
  STOP_OUT: 'STOP_OUT',
  SL: 'SL',
  TP: 'TP',
  ROLLOVER: 'ROLLOVER',
  COMMISSION: 'COMMISSION',
  CORRECTION: 'CORRECTION',
} as const;
export type DealType = (typeof DealType)[keyof typeof DealType];
export const DEAL_TYPES = Object.values(DealType) as readonly DealType[];

/** doc 02 §2.7 / I-10: deals that carry no `order_id`. */
export const ORDERLESS_DEAL_TYPES = [
  DealType.ROLLOVER,
  DealType.COMMISSION,
  DealType.CORRECTION,
] as const satisfies readonly DealType[];

/** doc 02 §2.8. Partial close keeps `OPEN`; there is no `PARTIALLY_CLOSED`. */
export const PositionState = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
} as const;
export type PositionState = (typeof PositionState)[keyof typeof PositionState];
export const POSITION_STATES = Object.values(PositionState) as readonly PositionState[];

/** doc 03 §3.3 / doc 04 §2.2 (`accounts.margin_mode`, reserved for v2). */
export const MarginMode = {
  HEDGED: 'HEDGED',
  NETTING: 'NETTING',
} as const;
export type MarginMode = (typeof MarginMode)[keyof typeof MarginMode];
export const MARGIN_MODES = Object.values(MarginMode) as readonly MarginMode[];

/** doc 04 §2.2 (`accounts.state`). */
export const AccountState = {
  ACTIVE: 'ACTIVE',
  READ_ONLY: 'READ_ONLY',
  DISABLED: 'DISABLED',
  ARCHIVED: 'ARCHIVED',
} as const;
export type AccountState = (typeof AccountState)[keyof typeof AccountState];
export const ACCOUNT_STATES = Object.values(AccountState) as readonly AccountState[];

/** doc 03 §5. */
export const CommissionMode = {
  PER_LOT_ROUND_TURN: 'PER_LOT_ROUND_TURN',
  PER_LOT_PER_SIDE: 'PER_LOT_PER_SIDE',
  PERCENT_NOTIONAL: 'PERCENT_NOTIONAL',
} as const;
export type CommissionMode = (typeof CommissionMode)[keyof typeof CommissionMode];
export const COMMISSION_MODES = Object.values(CommissionMode) as readonly CommissionMode[];

/** doc 03 §6.2. */
export const SwapMode = {
  POINTS: 'POINTS',
  PERCENT_ANNUAL: 'PERCENT_ANNUAL',
  BASE_CCY: 'BASE_CCY',
} as const;
export type SwapMode = (typeof SwapMode)[keyof typeof SwapMode];
export const SWAP_MODES = Object.values(SwapMode) as readonly SwapMode[];

/** Literal-membership guard, shared shape. Kept explicit per enum for narrowing. */
export function isOrderState(value: unknown): value is OrderState {
  return typeof value === 'string' && (ORDER_STATES as readonly string[]).includes(value);
}

export function isOrderSide(value: unknown): value is OrderSide {
  return typeof value === 'string' && (ORDER_SIDES as readonly string[]).includes(value);
}

export function isOrderType(value: unknown): value is OrderType {
  return typeof value === 'string' && (ORDER_TYPES as readonly string[]).includes(value);
}

export function isTimeInForce(value: unknown): value is TimeInForce {
  return typeof value === 'string' && (TIME_IN_FORCES as readonly string[]).includes(value);
}

export function isExecutionMode(value: unknown): value is ExecutionMode {
  return typeof value === 'string' && (EXECUTION_MODES as readonly string[]).includes(value);
}

export function isFillPolicy(value: unknown): value is FillPolicy {
  return typeof value === 'string' && (FILL_POLICIES as readonly string[]).includes(value);
}

export function isTriggerState(value: unknown): value is TriggerState {
  return typeof value === 'string' && (TRIGGER_STATES as readonly string[]).includes(value);
}

export function isDealType(value: unknown): value is DealType {
  return typeof value === 'string' && (DEAL_TYPES as readonly string[]).includes(value);
}

export function isPositionState(value: unknown): value is PositionState {
  return typeof value === 'string' && (POSITION_STATES as readonly string[]).includes(value);
}

export function isMarginMode(value: unknown): value is MarginMode {
  return typeof value === 'string' && (MARGIN_MODES as readonly string[]).includes(value);
}

export function isAccountState(value: unknown): value is AccountState {
  return typeof value === 'string' && (ACCOUNT_STATES as readonly string[]).includes(value);
}

export function isCommissionMode(value: unknown): value is CommissionMode {
  return typeof value === 'string' && (COMMISSION_MODES as readonly string[]).includes(value);
}

export function isSwapMode(value: unknown): value is SwapMode {
  return typeof value === 'string' && (SWAP_MODES as readonly string[]).includes(value);
}
